import {useCallback, useEffect, useRef, useState} from 'react';
import {auth} from '@src/firebase';
import {useMtnCheckReturnMutation} from '@src/Api/TraceApi/traceApi';
import {IDeviceInfo} from '@src/Api/TraceApi/endpoints/sessions.endpoint';
import useIsLoggedIn from '@src/Providers/AuthProvider/useIsLoggedIn';
import {isMtnSite} from '@src/Configs/mtnConfig';
import useMtnLoginCompletion from './useMtnLoginCompletion';

const RETURN_PARAMS = ['status', 'msisdn', 'transactionID'];

/**
 * True when MTN/Penrose sent the subscriber back with ?status=…&msisdn=….
 * Used by AuthGuardProvider to suspend auth enforcement during the return.
 */
export function isMtnReturnInProgress(): boolean {
  if (!isMtnSite()) return false;
  const params = new URLSearchParams(window.location.search);
  return params.has('msisdn') && params.has('status');
}

/**
 * Handles the return from the Penrose subscription page on the MTN site:
 * ?status=200&msisdn=233…  ->  POST /auth/mtn/check-return  ->  login.
 * The backend only logs in when Penrose says ACTIVE (and auto-login is on).
 */
export function useMtnAutoLogin() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasProcessedRef = useRef(false);

  const [checkReturn] = useMtnCheckReturnMutation();
  const completeLogin = useMtnLoginCompletion();
  const {isLoggedIn} = useIsLoggedIn();

  const processReturn = useCallback(async () => {
    if (hasProcessedRef.current || !isMtnReturnInProgress()) return;
    hasProcessedRef.current = true;

    const url = new URL(window.location.href);
    const status = url.searchParams.get('status') || '';
    const msisdn = url.searchParams.get('msisdn') || '';
    const transactionID = url.searchParams.get('transactionID') || undefined;

    // Clean the URL right away so a page refresh does not replay the return.
    RETURN_PARAMS.forEach(p => url.searchParams.delete(p));
    window.history.replaceState({}, '', url.toString());

    if (isLoggedIn || auth.currentUser) {
      console.log('[MTN Return] Already logged in, nothing to do');
      return;
    }

    setIsProcessing(true);
    setError(null);
    try {
      const deviceInfo: IDeviceInfo = {
        name: navigator.userAgent,
        platform: navigator.platform,
        browser: navigator.appName || 'Unknown',
        userAgent: navigator.userAgent,
      };
      const response = await checkReturn({
        status,
        msisdn,
        transactionID,
        deviceInfo,
      }).unwrap();

      if (response.action !== 'allow_login') {
        console.log('[MTN Return] No login:', response.reason);
        return;
      }
      await completeLogin(response);
      console.log('[MTN Return] Logged in');
    } catch (err: any) {
      console.error('[MTN Return] Failed:', err);
      setError(err?.message || 'Authentication failed');
    } finally {
      setIsProcessing(false);
    }
  }, [checkReturn, completeLogin, isLoggedIn]);

  useEffect(() => {
    processReturn();
  }, [processReturn]);

  return {isProcessing, error};
}

export default useMtnAutoLogin;
